import { useEffect, useState, useMemo } from "react";
import { getBreakpoints } from "@core/utils/getBreakpoints";

type BreakpointKey = "sm" | "md" | "lg" | "xl" | "2xl";

export function useBreakpoint(breakpointKey: BreakpointKey) {
  const breakpoints = useMemo(() => getBreakpoints(), []);

  const breakpointValue = breakpoints[breakpointKey];

  // Read the screen on the first render rather than after it, so a component
  // that lays itself out by the breakpoint does not first draw, and fetch for,
  // the smallest screen and then redo it.
  const [isActive, setIsActive] = useState(
    () =>
      !!breakpointValue &&
      typeof window !== "undefined" &&
      typeof window.matchMedia === "function" &&
      window.matchMedia(`(min-width: ${breakpointValue}px)`).matches
  );

  useEffect(() => {
    if (!breakpointValue) return;

    const mediaQuery = window.matchMedia(`(min-width: ${breakpointValue}px)`);

    setIsActive(mediaQuery.matches);

    const handler = (e: MediaQueryListEvent) => setIsActive(e.matches);

    mediaQuery.addEventListener("change", handler);

    return () => mediaQuery.removeEventListener("change", handler);
  }, [breakpointKey, breakpointValue]);

  return isActive;
}

